// Libs
import React from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '@material-ui/core';

// Components
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';

// Actions

// Utils
import useTranslator from 'UTILS/hooks/useTranslator';
import {
  CONNECTIVITY_CHECK,
  DONE,
  ERROR_LEVEL,
  LEVEL_COLORS,
  SUCCESS_LEVEL,
  WARNING_LEVEL,
} from 'UTILS/constants';
import translations from './locales.json';
import {
  CRITICAL_MAX_LATENCY,
  CRITICAL_MAX_PACKET_LOSS_PERCENT,
  CRITICAL_MIN_QUALITY,
  MAX_LATENCY,
  MAX_PACKET_LOSS_PERCENT,
  MIN_QUALITY,
} from 'CHECKS/connectivity/connectivityCheck';

const formatValue = (value) => {
  if (value == null || (typeof value === 'number' && isNaN(value))) {
    return '-';
  }
  return value?.toString();
};

const ConnectivityDiagnostic = () => {
  const theme = useTheme();
  const connectivityCheck = useSelector(state => state.checks[CONNECTIVITY_CHECK]);
  const [translate] = useTranslator(translations);

  const isVerbose = useSelector(state => state.app.isVerbose);

  const detailList = Object.entries(connectivityCheck.resultDetails);

  const latencyLevel = connectivityCheck.resultDetails.latency <= MAX_LATENCY ? SUCCESS_LEVEL : connectivityCheck.resultDetails.latency <= CRITICAL_MAX_LATENCY ? WARNING_LEVEL : ERROR_LEVEL;
  // Single results should not appear as worse than the global result.
  const displayedLatencyLevel = Math.min(latencyLevel, connectivityCheck.level);
  const LatencyIcon = displayedLatencyLevel === SUCCESS_LEVEL ? CheckCircleIcon : ErrorIcon;

  const packetLossPercentLevel = connectivityCheck.resultDetails.packetLossPercent <= MAX_PACKET_LOSS_PERCENT ? SUCCESS_LEVEL : connectivityCheck.resultDetails.packetLossPercent <= CRITICAL_MAX_PACKET_LOSS_PERCENT ? WARNING_LEVEL : ERROR_LEVEL;
  const qualityLevel = connectivityCheck.resultDetails.quality >= MIN_QUALITY ? SUCCESS_LEVEL : connectivityCheck.resultDetails.quality >= CRITICAL_MIN_QUALITY ? WARNING_LEVEL : ERROR_LEVEL;
  // In the UI, quality implicitly includes packet loss
  const userQualityLevel = Math.max(qualityLevel, packetLossPercentLevel);
  // Single results should not appear as worse than the global result which is a combination of all of them.
  const displayedQualityLevel = Math.min(userQualityLevel, connectivityCheck.level);
  const QualityIcon = displayedQualityLevel === SUCCESS_LEVEL ? CheckCircleIcon : ErrorIcon;

  return (
    <>
      {isVerbose && detailList.length > 0 && (
        <Table size='small' className='connectivityDiagnostic-table'>
          <TableBody>
            {detailList.map(([label, value]) => (
              <TableRow key={label}>
                <TableCell scope='row'>{translate(label)}</TableCell>
                <TableCell align='right'>{formatValue(value)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {!isVerbose && connectivityCheck.stage === DONE && (
        <Box className='connectivityDiagnostic-results'>
          <Box className='connectivityDiagnostic-resultItem'>
            <LatencyIcon className='connectivityDiagnostic-resultItemIcon' htmlColor={theme.palette[LEVEL_COLORS[displayedLatencyLevel]].dark} style={{ fontSize: '55px' }} />
            <Typography className='connectivityDiagnostic-resultItemText'>{translate('latencyNoUnit')}</Typography>
          </Box>
          <Box className='connectivityDiagnostic-resultItem'>
            <QualityIcon className='connectivityDiagnostic-resultItemIcon' htmlColor={theme.palette[LEVEL_COLORS[displayedQualityLevel]].dark} style={{ fontSize: '55px' }} />
            <Typography className='connectivityDiagnostic-resultItemText'>{translate('qualityNoUnit')}</Typography>
          </Box>
        </Box>
      )}
    </>
  );
};

export default ConnectivityDiagnostic;
